(function () {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  // Ensure utils is loaded before proceeding
  if (typeof window.requireUtils !== 'function') {
    console.error(
      '[checkout.js] window.requireUtils is not defined. utils.js may not have loaded.'
    );
    return;
  }

  try {
    window.requireUtils('checkout.js');
  } catch (e) {
    console.error('[checkout.js] Dependency check failed:', e.message);
    return; // Fail silently if utils not available (prevents further errors)
  }

  const cartDrawer = document.getElementById('cart-drawer');
  const cartItemsContainer = document.getElementById('cart-drawer-items');
  const cartCheckoutButton = document.getElementById('cart-drawer-checkout-button');
  const cartDrawerActions = document.getElementById('cart-drawer-actions');

  if (!cartDrawer || !cartItemsContainer || !cartCheckoutButton) {
    console.warn('[checkout.js] Required cart drawer elements not found in DOM');
    return;
  }

  /**
   * When the cart drawer opens, prevent sticky elements from causing scroll issues.
   */
  function getCartDrawerDialog() {
    const contentEl = cartDrawer.querySelector('ck-drawer-content');
    return contentEl?.shadowRoot?.querySelector?.('dialog') || null;
  }

  function isCartDrawerOpen() {
    const dlg = getCartDrawerDialog();
    if (!dlg) return false;
    return dlg.hasAttribute('open') || dlg.getAttribute('data-status') === 'open';
  }

  // Store refresh cart timer for cleanup
  let refreshCartTimer = null;

  function syncDrawerState() {
    const isOpen = isCartDrawerOpen();
    document.documentElement.setAttribute('data-cart-drawer-open', isOpen ? 'true' : 'false');

    // Clean up refresh timer when drawer closes
    if (!isOpen && refreshCartTimer) {
      clearTimeout(refreshCartTimer);
      refreshCartTimer = null;
    }
  }

  // Observe the internal dialog state to know when the drawer opens/closes.
  (function attachCartDrawerObserver(attempt = 0) {
    const dlg = getCartDrawerDialog();
    if (!dlg) {
      if (attempt < 60) requestAnimationFrame(() => attachCartDrawerObserver(attempt + 1));
      return;
    }

    syncDrawerState();
    new MutationObserver(syncDrawerState).observe(dlg, {
      attributes: true,
      attributeFilter: ['open', 'data-status'],
    });
  })();

  cartDrawer.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="/cart"], a[href$="/cart"]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation();
  });

  // Listen for change events on subscription select
  cartItemsContainer.addEventListener('change', (e) => {
    const select = e.target;
    if (!select?.classList?.contains('ck-cart-subscription-select')) return;

    const lineItemKey = select.dataset.cartItemKey;
    if (!lineItemKey) return;

    const value = select.value;
    // Store current value as previous for potential revert
    const previousValue = select.dataset.currentPlanId || select.value || 'onetime';
    select.dataset.previousValue = previousValue;

    // Show loading state
    select.disabled = true;
    select.dataset.loading = 'true';

    // Add spinner to subscription select - position it relative to the select itself
    let spinner = select.parentElement?.querySelector('ck-spinner[data-subscription-spinner]');
    if (!spinner) {
      spinner = document.createElement('ck-spinner');
      spinner.setAttribute('data-subscription-spinner', 'true');
      spinner.setAttribute('size', 'sm');
      spinner.className =
        'absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none z-10 text-foreground';
      const selectParent = select.parentElement;
      if (selectParent) {
        selectParent.style.position = 'relative';
        selectParent.appendChild(spinner);
      }
    }

    const cartItemEl = cartItemsContainer.querySelector(`[data-cart-item-key="${lineItemKey}"]`);
    if (cartItemEl && window.cartSubscription) {
      // Sync Appstle widget - Appstle will handle Shopify update
      window.cartSubscription.handleSubscriptionChange(cartItemEl, select, value, previousValue);
    } else {
      // If no Appstle widget, revert and show error
      select.value = previousValue;
      select.disabled = false;
      delete select.dataset.loading;
      if (spinner) spinner.remove();
      window.ckToast?.error('subscription', 'Subscription widget not available', {
        title: 'Could not update subscription',
      });
    }
  });

  /**
   * Track line items that hit inventory errors (e.g. out of stock).
   * This lets us render a clear "disabled" state even if Shopify's cart payload
   * doesn't expose availability consistently across stores.
   */
  const outOfStockItemKeys = new Set();
  let hasUnavailableItems = false;

  /**
   * Keep drawer actions spacing consistent:
   * - When delivery date section is hidden/unavailable: remove top border and tighten padding.
   */
  function updateDrawerActionsForDeliveryVisibility() {
    if (!cartDrawerActions) return;
    const deliverySection = document.getElementById('cart-drawer-delivery');
    const deliveryIsVisible = !!deliverySection && !deliverySection.classList.contains('hidden');

    if (deliveryIsVisible) {
      cartDrawerActions.setAttribute('data-delivery-visible', 'true');
    } else {
      cartDrawerActions.removeAttribute('data-delivery-visible');
    }
  }

  // Get configuration from data attributes
  // Ensure .js extension for Cart API endpoints (Shopify requires .js for JSON responses)
  let cartChangeUrl = cartDrawer.getAttribute('data-cart-change-url') || '/cart/change.js';
  if (!cartChangeUrl.endsWith('.js')) {
    cartChangeUrl = cartChangeUrl + '.js';
  }
  let cartAddUrl = cartDrawer.getAttribute('data-cart-add-url') || '/cart/add.js';
  if (!cartAddUrl.endsWith('.js')) {
    cartAddUrl = cartAddUrl + '.js';
  }

  const config = {
    enabledPaymentTypes: JSON.parse(cartDrawer.getAttribute('data-payment-types') || '[]'),
    paymentIconUrls: JSON.parse(cartDrawer.getAttribute('data-payment-icons') || '{}'),
    cartUrl: cartDrawer.getAttribute('data-cart-url') || '/cart',
    cartChangeUrl,
    cartAddUrl,
    locale: cartDrawer.getAttribute('data-locale') || undefined,
    currency: cartDrawer.getAttribute('data-currency') || 'USD',
    emptyCartImage: cartDrawer.getAttribute('data-empty-cart-image') || '',
    deliveryWeeksStart: parseInt(cartDrawer.getAttribute('data-delivery-weeks-start') || '2', 10),
    deliveryWeeksEnd: parseInt(cartDrawer.getAttribute('data-delivery-weeks-end') || '3', 10),
    emptyCartMessage:
      cartDrawer.getAttribute('data-empty-cart-message') ||
      "Can't see nothing in your cart.\nThe cup needs a refill and so do you.",
    emptyCartCta: cartDrawer.getAttribute('data-empty-cart-cta') || "Let's fill it up!",
  };

  // Cache product JSON requests so we don't refetch per render
  // ------------------------------
  // Subscription dropdown helpers
  // ------------------------------
  function getSellingPlanIdFromCartItem(item) {
    return window.utils.getSellingPlanIdFromCartItem(item);
  }

  function getProductHandleFromItem(item) {
    return window.utils.getProductHandleFromItem(item);
  }

  /**
   * Refresh cart after Appstle updates subscription
   * Called by cart-subscription.js after Appstle events fire
   */
  async function refreshCartAfterSubscriptionChange(lineItemKey, selectedPlanId) {
    try {
      const cart = await fetchCart();
      if (!cart) return;

      // Update subscription select's data attribute and value to reflect the applied plan
      // Note: Try to find by lineItemKey first, but if not found, search all selects
      // (Shopify may have changed the line item key)
      let subscriptionSelect = cartItemsContainer.querySelector(
        `select.ck-cart-subscription-select[data-cart-item-key="${lineItemKey}"]`
      );

      // If not found by key, try to find by variant_id from the cart item
      if (!subscriptionSelect) {
        const updatedItem = cart.items?.find((item) => item.key === lineItemKey);
        if (updatedItem) {
          // Find select by variant_id (more reliable than key)
          const allSelects = cartItemsContainer.querySelectorAll(
            'select.ck-cart-subscription-select'
          );
          for (const select of allSelects) {
            const selectVariantId = select
              .closest('[data-cart-item-key]')
              ?.querySelector('[data-cart-item-id]')
              ?.getAttribute('data-cart-item-id');
            if (selectVariantId && String(updatedItem.variant_id) === selectVariantId) {
              subscriptionSelect = select;
              // Update the data-cart-item-key to match the new key
              subscriptionSelect.dataset.cartItemKey = lineItemKey;
              break;
            }
          }
        }
      }

      if (subscriptionSelect) {
        // Update select value to match the selected plan
        const selectValue =
          selectedPlanId && selectedPlanId !== 'onetime' ? selectedPlanId : 'onetime';
        subscriptionSelect.value = selectValue;

        if (selectedPlanId && selectedPlanId !== 'onetime') {
          subscriptionSelect.dataset.currentPlanId = String(selectedPlanId);
          subscriptionSelect.dataset.synced = 'true';
        } else {
          // For "onetime", explicitly set currentPlanId to empty and mark as synced
          // This tells hydrateSubscriptionSelects to respect the "onetime" choice
          subscriptionSelect.dataset.currentPlanId = '';
          subscriptionSelect.dataset.synced = 'true'; // Mark as synced even for one-time
          // Ensure the select value is explicitly "onetime"
          subscriptionSelect.value = 'onetime';
        }
        subscriptionSelect.disabled = false;
        delete subscriptionSelect.dataset.loading;

        // Remove spinner
        const spinner = subscriptionSelect.parentElement?.querySelector(
          'ck-spinner[data-subscription-spinner]'
        );
        if (spinner) spinner.remove();
      }

      // Save the synced state BEFORE rendering (so we can restore it after renderCartItems)
      // renderCartItems will recreate the HTML, so we need to preserve the user's explicit choice
      let savedSyncedState = null;
      if (subscriptionSelect && subscriptionSelect.dataset.synced === 'true') {
        savedSyncedState = {
          synced: true,
          currentPlanId: subscriptionSelect.dataset.currentPlanId || '',
          selectValue: subscriptionSelect.value,
        };
      }

      renderCartItems(cart);
      updateCartCount(cart);

      // AFTER rendering, restore the synced state for this item
      // This ensures the "onetime" choice persists even when cart item has no selling_plan_allocation
      if (savedSyncedState) {
        const newSelect = cartItemsContainer.querySelector(
          `select.ck-cart-subscription-select[data-cart-item-key="${lineItemKey}"]`
        );
        if (newSelect) {
          newSelect.dataset.synced = 'true';
          newSelect.dataset.currentPlanId = savedSyncedState.currentPlanId;
          newSelect.value = savedSyncedState.selectValue;
        }
      }

      // Hydrate subscription selects (will respect the synced state we just restored)
      if (window.cartSubscription) {
        window.cartSubscription.hydrateSubscriptionSelects();
      }
    } catch (err) {
      console.error('[checkout.js] Error refreshing cart after subscription change:', err);
      window.ckToast?.error('subscription', 'Failed to refresh cart', {
        title: 'Could not update subscription',
      });
    }
  }

  // Handle checkout button click - navigate to checkout
  function handleCheckoutClick(e) {
    try {
      const button = document.getElementById('cart-drawer-checkout-button');
      if (!button) return;

      // Check if button is disabled (empty cart)
      if (button.hasAttribute('disabled')) {
        e.preventDefault();
        e.stopPropagation();

        // If disabled due to unavailable items, inform user via toast.
        if (hasUnavailableItems) {
          window.ckToast?.error(
            'updateCart',
            { message: 'Remove unavailable item(s) from your cart to checkout.' },
            { title: 'Out of stock' }
          );
        }
        return;
      }

      // Navigate to checkout
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[checkout.js] Error in handleCheckoutClick:', error);
    }
  }

  // Set onClick property on the custom element (button component supports this)
  if (cartCheckoutButton) {
    cartCheckoutButton.onClick = handleCheckoutClick;
  }

  // Also add event listener as fallback
  cartCheckoutButton.addEventListener('click', handleCheckoutClick);

  /**
   * Fetch cart data from Shopify
   */
  async function fetchCart() {
    try {
      const cartUrl = `${config.cartUrl}.js`;
      const response = await fetch(cartUrl);
      if (!response.ok) {
        console.error(
          `[checkout.js] Failed to fetch cart from ${cartUrl}:`,
          response.status,
          response.statusText
        );
        throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      }
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('[checkout.js] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Format money from cents
   */
  function formatMoney(cents) {
    return window.utils.formatMoney(cents, {
      locale: config.locale,
      currency: config.currency,
    });
  }

  /**
   * Render payment icons from Shopify enabled payment types
   */
  function renderPaymentIcons() {
    if (!config.enabledPaymentTypes || config.enabledPaymentTypes.length === 0) return '';

    return config.enabledPaymentTypes
      .map((paymentType) => {
        const typeKey = paymentType.toLowerCase().replace(/\s+/g, '_');
        const iconUrl = config.paymentIconUrls[typeKey];

        if (!iconUrl) {
          // If no icon mapping found, skip this payment type
          return '';
        }

        return `<img src='${iconUrl}' alt='${paymentType}' class='h-6' loading='lazy' onerror='this.style.display="none"' />`;
      })
      .filter((html) => html !== '') // Remove empty strings
      .join('');
  }

  /**
   * Format properties and variants for display
   * Returns: { metafieldValues: string[], variantTitles: string[] }
   */
  function formatPropertiesAndVariants(properties, item) {
    const metafieldValues = [];
    const variantTitles = [];

    // Extract variant titles from item
    if (item.variant_title && item.variant_title !== 'Default Title') {
      variantTitles.push(item.variant_title);
    } else if (item.title && item.title.includes(' - ')) {
      // Parse variant from title if variant_title is not available
      const parts = item.title.split(' - ');
      if (parts.length > 1) {
        variantTitles.push(parts.slice(1).join(' - '));
      }
    }

    // Extract metafield property values from properties
    if (properties) {
      // Handle array format (Shopify cart API format)
      if (Array.isArray(properties)) {
        properties.forEach((prop) => {
          const key = prop.first || prop.name || '';
          const value = prop.last || prop.value || '';

          // Skip empty values and variant title property
          if (
            key &&
            value &&
            String(value).trim() !== '' &&
            key.toLowerCase() !== 'variant title'
          ) {
            metafieldValues.push(String(value).trim());
          }
        });
      }
      // Handle object format (key-value pairs)
      else if (typeof properties === 'object') {
        Object.entries(properties).forEach(([key, value]) => {
          // Skip empty values and variant title property
          if (
            key &&
            value &&
            String(value).trim() !== '' &&
            key.toLowerCase() !== 'variant title'
          ) {
            metafieldValues.push(String(value).trim());
          }
        });
      }
    }

    return { metafieldValues, variantTitles };
  }

  /**
   * Get order summary HTML (returns HTML string, doesn't render directly)
   */
  function getOrderSummaryHtml(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return '';
    }

    const itemCount = cart.item_count || 0;
    const subtotal = cart.total_price || 0;
    // Shopify doesn't provide shipping estimate in cart API, so we'll use a placeholder
    // You can customize this based on your shipping logic
    const shippingEstimate = 0; // This would need to be calculated based on your shipping rules
    const total = subtotal + shippingEstimate;

    return `
      <div id='cart-drawer-order-summary' class='p-(--drawer-p)'>
        <h4 class='font-mono! text-foreground text-sm uppercase mb-5 tracking-wide'>
          ORDER SUMMARY
        </h4>
        <div class='space-y-1.5 mb-1.5'>
          <div class='flex items-center justify-between text-sm text-foreground-secondary'>
            <span class='inline-flex items-center gap-1.5'>Subtotal <span class='font-mono text-foreground-tertiary text-xs border-border border px-1.5 py-0.5'> ${itemCount} ${itemCount === 1 ? 'ITEM' : 'ITEMS'}</span></span>
            <span class='text-right'>${formatMoney(subtotal)}</span>
          </div>
          <div class='flex items-center justify-between text-sm text-foreground-secondary'>
            <span>Estimated shipping</span>
            <span class='text-right'>${
              shippingEstimate > 0 ? formatMoney(shippingEstimate) : 'Calculated at checkout'
            }</span>
          </div>
        </div>
        <div class='flex items-center justify-between text-sm text-foreground'>
          <span>Total (VAT included)</span>
          <span class='text-right'>${formatMoney(total)}</span>
        </div>
        <div class='mt-5'>
          <div class='flex items-center gap-2 flex-wrap'>
            ${renderPaymentIcons()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render cart items
   */
  function renderCartItems(cart) {
    try {
      const deliverySection = document.getElementById('cart-drawer-delivery');

      if (!cart || !cart.items || cart.items.length === 0) {
        cartItemsContainer.innerHTML = `
        <div class='flex flex-col'>
        <p class='p-5 text-foreground/50 text-sm leading-normal'>
          ${(() => {
            const formattedMessage = config.emptyCartMessage
              .split('\n')
              .map((line, index) => {
                if (index === 0) {
                  return `<span class='text-foreground'>${line}</span>`;
                }
                return line;
              })
              .join(' <br>');
            return formattedMessage;
          })()}
        </p>

        <img
          src='${config.emptyCartImage}'
          alt='Cart empty'
          class='w-40 h-auto mx-auto mb-8 object-cover'
          width='160'
          height='160'
        >

        <p class='text-foreground text-sm leading-normal p-5'>${config.emptyCartCta}</p>
      </div>
      `;
        // Use innerHTML to preserve button structure when loading
        if (!cartCheckoutButton.loading) {
          cartCheckoutButton.innerHTML = 'Checkout';
        }
        cartCheckoutButton.setAttribute('disabled', 'true');

        // Hide delivery section when cart is empty
        if (deliverySection) {
          deliverySection.classList.add('hidden');
        }

        // Hide upsell section when cart is empty
        const upsellSection = document.getElementById('cart-drawer-upsell');
        if (upsellSection) {
          upsellSection.classList.add('hidden');
        }

        updateDrawerActionsForDeliveryVisibility();
        return;
      }

      // Remove stale keys (items removed from cart since last inventory error)
      const currentItemKeys = new Set(cart.items.map((i) => i.key));
      for (const key of outOfStockItemKeys) {
        if (!currentItemKeys.has(key)) outOfStockItemKeys.delete(key);
      }

      // Enable button when cart has items
      cartCheckoutButton.removeAttribute('disabled');

      updateDrawerActionsForDeliveryVisibility();

      const itemsHtml = cart.items
        .map((item) => {
          const handle = getProductHandleFromItem(item);
          const currentPlanId = getSellingPlanIdFromCartItem(item) || '';

          const { metafieldValues, variantTitles } = formatPropertiesAndVariants(
            item.properties,
            item
          );

          // Only consider item unavailable if inventory is tracked AND it's unavailable
          // If inventory_quantity is null/undefined, inventory is not tracked and item should be available
          const inventoryIsTracked =
            item.inventory_quantity !== null && item.inventory_quantity !== undefined;
          const itemIsUnavailable =
            outOfStockItemKeys.has(item.key) ||
            (inventoryIsTracked && (item.available === false || item.variant_available === false));

          const itemUrl = String(item.url || item.product_url || '').replace(/'/g, '&apos;');

          // Use product title if available, otherwise extract product name from title
          // Shopify cart API: item.title includes variant (e.g., "Product Name - Variant")
          // item.product_title should be just the product name, but if not available, parse it
          let productName = item.product_title || item.title;

          // If title includes variant separator, extract just the product name
          if (productName.includes(' - ')) {
            productName = productName.split(' - ')[0];
          }

          const escapedTitle = productName
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/'/g, '&apos;');

          // Combine metafield values and variant titles
          const allDetails = [...metafieldValues, ...variantTitles];
          const detailsText =
            allDetails.length > 0
              ? allDetails
                  .map((detail) => detail.replace(/</g, '&lt;').replace(/>/g, '&gt;'))
                  .join(', ')
              : '';

          return `
      <div
        class='p-4 lg:p-0 flex gap-4 border-b border-border-soft items-stretch'
        data-cart-item-key='${item.key}'
        data-original-price-cents='${item.original_price || item.price || 0}'
      >
        <div class='shrink-0'>
          ${
            item.image
              ? `<a href='${itemUrl}' class='block h-full' aria-label='View ${escapedTitle}'>
                  <img
                    src='${item.image}'
                    alt='${escapedTitle}'
                    width='107'
                    height='107'
                    class='w-22.5 lg:w-[107px] h-full object-cover bg-background-product'
                    loading='lazy'
                  />
                </a>`
              : `<a href='${itemUrl}' class='block w-22.5 lg:w-[107px] h-full bg-background-product' aria-label='View ${escapedTitle}'></a>`
          }
        </div>
        <div class='flex-1 min-w-0 p-0 lg:p-5'>
          <div class='mb-6'>
            <div class='flex justify-between gap-4 mb-0'>
              <a
                href='${itemUrl}'
                class='block min-w-0'
                aria-label='View ${escapedTitle}'
              >
                <h3 class='font-header uppercase font-black text-foreground text-sm mb-0'>
                  ${escapedTitle}
                </h3>
                ${
                  detailsText
                    ? `<p class='text-foreground-secondary normal-case text-sm mt-0'>${detailsText}</p>`
                    : ''
                }
              </a>
              <div class='flex flex-col items-end gap-0.5' data-price-container>
                ${(() => {
                  // Calculate original line price for discounts
                  // Priority: subscription compare_at_price > product original_line_price
                  let originalLinePrice = item.original_line_price;

                  if (item.selling_plan_allocation?.compare_at_price) {
                    originalLinePrice =
                      item.selling_plan_allocation.compare_at_price * item.quantity;
                  }

                  if (originalLinePrice && originalLinePrice > item.final_line_price) {
                    return `
                    <span class='text-sm font-header uppercase font-black text-foreground line-through opacity-30 leading-none'>${formatMoney(
                      originalLinePrice
                    )}</span>
                    <span class='text-sm font-header uppercase font-black text-foreground leading-none'>${formatMoney(
                      item.final_line_price
                    )}</span>
                  `;
                  }
                  return `
                    <span class='text-sm font-header uppercase font-black text-foreground leading-none'>${formatMoney(
                      item.final_line_price
                    )}</span>
                  `;
                })()}
              </div>
            </div>
          </div>
          <div class='flex flex-col items-start'>
            <div class='flex items-center gap-2 w-full min-w-0'>
              <div class='flex items-center border border-border shrink-0'>
                <button
                  type='button'
                  class='cursor-pointer px-2 py-1.5 text-foreground hover:bg-background-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  data-quantity-decrease
                  data-cart-item-key='${item.key}'
                  ${itemIsUnavailable ? 'disabled' : ''}
                  aria-label='Decrease quantity'
                >
                  <ck-icon name='minus' size='sm'></ck-icon>
                </button>
                <input
                  type='number'
                  min='1'
                  value='${item.quantity}'
                  data-cart-item-key='${item.key}'
                  data-cart-item-id='${item.id}'
                  class='w-12 text-center text-sm border-0 focus:outline-none focus:ring-0 bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                  ${itemIsUnavailable ? 'disabled' : ''}
                  aria-label='Quantity'
                />
                <button
                  type='button'
                  class='cursor-pointer px-2 py-1.5 text-foreground hover:bg-background-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                  data-quantity-increase
                  data-cart-item-key='${item.key}'
                  ${itemIsUnavailable ? 'disabled' : ''}
                  aria-label='Increase quantity'
                >
                  <ck-icon name='plus' size='sm'></ck-icon>
                </button>
              </div>

              ${
                handle
                  ? `<select
                      class='ck-cart-subscription-select grow text-sm border border-border px-3 py-2 pr-8 bg-background appearance-none cursor-pointer hover:border-border-hard focus:outline-none focus:ring-1 focus:ring-border-hard min-w-0 disabled:opacity-50 disabled:cursor-wait'
                      data-cart-item-key='${item.key}'
                      data-product-handle='${handle}'
                      data-current-plan-id='${currentPlanId}'
                    >
                      <option value='onetime'>One time</option>
                    </select>`
                  : ''
              }
            </div>

            ${
              item.inventory_quantity !== null && item.inventory_quantity !== undefined
                ? (() => {
                    const normalizedQty = window.utils?.normalizeInventoryQuantity
                      ? window.utils.normalizeInventoryQuantity(item.inventory_quantity)
                      : Math.max(0, item.inventory_quantity || 0);
                    // Only show inventory quantity if it's tracked (not null) and 5 or less
                    // When inventory is not tracked, inventory_quantity will be null/undefined
                    return normalizedQty <= 5
                      ? `<p class='text-sm text-foreground-secondary mt-2' data-inventory-quantity='${normalizedQty}'>
                          <span class='font-semibold'>${normalizedQty}</span> left in stock
                        </p>`
                      : '';
                  })()
                : ''
            }
            ${
              itemIsUnavailable
                ? `<p class='mt-2 text-xs font-mono uppercase tracking-wide text-red-600'>Out of stock</p>`
                : ''
            }
          </div>
        </div>
      </div>
    `;
        })
        .join('');

      // Get order summary HTML
      const orderSummaryHtml = getOrderSummaryHtml(cart);

      // Set items and order summary together
      cartItemsContainer.innerHTML = itemsHtml + orderSummaryHtml;

      // Show/hide upsell section based on cart state
      const upsellSection = document.getElementById('cart-drawer-upsell');
      const orderSummary = document.getElementById('cart-drawer-order-summary');

      function updateOrderSummaryPosition() {
        if (!orderSummary) return;

        // Check if upsell section exists and is visible
        const hasUpsell = upsellSection && !upsellSection.classList.contains('hidden');

        if (hasUpsell) {
          // Upsell is visible, add border-bottom to order summary
          orderSummary.classList.add('border-b', 'border-border');
          orderSummary.classList.remove('mt-auto');
        } else {
          // No upsell, stick order summary to bottom and remove border
          orderSummary.classList.remove('border-b', 'border-border');
          orderSummary.classList.add('mt-auto');
        }
      }

      if (upsellSection) {
        if (cart.items && cart.items.length > 0) {
          // Cart has items, upsell section visibility is controlled by Selleasy widget loading
          // The snippet's script will handle showing/hiding based on widget content
          // Watch for changes in upsell visibility
          const observer = new MutationObserver(() => {
            updateOrderSummaryPosition();
          });
          observer.observe(upsellSection, {
            attributes: true,
            attributeFilter: ['class'],
          });
        } else {
          // Cart is empty, hide upsell section
          upsellSection.classList.add('hidden');
        }
      }

      // Initial update
      updateOrderSummaryPosition();

      // Hydrate subscription selects and initialize Appstle widgets
      if (window.cartSubscription) {
        window.cartSubscription.hydrateSubscriptionSelects();
      }

      // Update checkout button text
      const subtotal = cart.total_price || 0;
      const shippingEstimate = 0;
      const unavailableItemsTotal = (cart.items || []).reduce((acc, item) => {
        // Only consider item unavailable if inventory is tracked AND it's unavailable
        // If inventory_quantity is null/undefined, inventory is not tracked and item should be available
        const inventoryIsTracked =
          item.inventory_quantity !== null && item.inventory_quantity !== undefined;
        const itemIsUnavailable =
          outOfStockItemKeys.has(item.key) ||
          (inventoryIsTracked && (item.available === false || item.variant_available === false));

        return itemIsUnavailable ? acc + (item.final_line_price || 0) : acc;
      }, 0);

      // Exclude unavailable line items from the checkout price shown on the button.
      // (Shopify may still include them in cart.total_price until the user removes/adjusts.)
      const adjustedSubtotal = Math.max(0, subtotal - unavailableItemsTotal);
      const total = adjustedSubtotal + shippingEstimate;
      const checkoutText = `CHECKOUT - ${formatMoney(total)}`;
      // Use innerHTML to preserve button structure when loading
      if (!cartCheckoutButton.loading) {
        cartCheckoutButton.innerHTML = checkoutText;
      }

      // Disable checkout if any unavailable items are present
      hasUnavailableItems = unavailableItemsTotal > 0;
      if (hasUnavailableItems) {
        cartCheckoutButton.setAttribute('disabled', 'true');
        cartCheckoutButton.setAttribute('aria-disabled', 'true');
      } else {
        cartCheckoutButton.removeAttribute('disabled');
        cartCheckoutButton.removeAttribute('aria-disabled');
      }

      // Wire up quantity change handlers
      cartItemsContainer.querySelectorAll('input[data-cart-item-key]').forEach((input) => {
        const handleChange = () => {
          const key = input.getAttribute('data-cart-item-key');
          const quantity = parseInt(input.value || '1', 10);
          if (quantity < 1) {
            removeCartItem(key);
          } else {
            updateCartItem(key, quantity, key);
          }
        };

        input.addEventListener('change', handleChange);
        input.addEventListener('input', handleChange);
      });

      // Wire up quantity buttons
      cartItemsContainer.querySelectorAll('[data-quantity-increase]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.getAttribute('data-cart-item-key');
          const input = cartItemsContainer.querySelector(`input[data-cart-item-key="${key}"]`);
          if (input) {
            const current = parseInt(input.value || '1', 10);
            input.value = current + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      cartItemsContainer.querySelectorAll('[data-quantity-decrease]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.getAttribute('data-cart-item-key');
          const input = cartItemsContainer.querySelector(`input[data-cart-item-key="${key}"]`);
          if (input) {
            const current = parseInt(input.value || '1', 10);
            if (current > 1) {
              input.value = current - 1;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              removeCartItem(key);
            }
          }
        });
      });

      // Wire up remove item handlers
      cartItemsContainer.querySelectorAll('[data-cart-remove-item]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const key = btn.getAttribute('data-cart-remove-item');
          removeCartItem(key);
        });
      });
    } catch (error) {
      console.error('[checkout.js] Error in renderCartItems:', error);
      console.error('[checkout.js] Cart data:', cart);
      // Render empty state on error
      cartItemsContainer.innerHTML = `
        <div class='p-5 text-foreground/50 text-sm'>
          <p>Error loading cart items. Please refresh the page.</p>
        </div>
      `;
    }
  }

  /**
   * Update cart item quantity and optionally properties
   */
  async function updateCartItem(lineItemKey, quantity, key, properties = null) {
    if (quantity < 1) {
      removeCartItem(lineItemKey);
      return;
    }

    // Show loading state on quantity input
    const cartItem = cartItemsContainer.querySelector(`[data-cart-item-key="${key}"]`);
    const quantityInput = cartItem?.querySelector('input[data-cart-item-key]');
    const checkoutButton = document.getElementById('cart-drawer-checkout-button');

    // Add spinner to quantity input
    let spinner = cartItem?.querySelector('ck-spinner[data-quantity-spinner]');
    if (!spinner && quantityInput) {
      spinner = document.createElement('ck-spinner');
      spinner.setAttribute('data-quantity-spinner', 'true');
      spinner.setAttribute('size', 'sm');
      spinner.className = 'absolute inset-0 flex items-center justify-center bg-background/80 z-10';
      if (quantityInput.parentElement) {
        quantityInput.parentElement.style.position = 'relative';
        quantityInput.parentElement.appendChild(spinner);
      }
    }

    // Show loading on checkout button
    if (checkoutButton) {
      checkoutButton.loading = true;
    }

    try {
      const updateData = {
        id: lineItemKey,
        quantity: quantity,
      };

      // Include properties if provided (this will overwrite existing properties)
      if (properties && Object.keys(properties).length > 0) {
        updateData.properties = properties;
      }

      const response = await fetch(config.cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.description || errorData.message || 'Failed to update cart. Please try again.';
        const lowerMessage = errorMessage.toLowerCase();

        // Better error messages for inventory issues
        if (response.status === 422 && key) {
          // Check if it's an inventory/quantity issue
          const isInventoryIssue =
            lowerMessage.includes('inventory') ||
            lowerMessage.includes('quantity') ||
            lowerMessage.includes('stock') ||
            lowerMessage.includes('available');

          if (isInventoryIssue) {
            // Try to get inventory quantity from the cart item (re-query since cartItem may have changed)
            const inventoryCartItem = cartItemsContainer.querySelector(
              `[data-cart-item-key="${key}"]`
            );
            const inventoryEl = inventoryCartItem?.querySelector('[data-inventory-quantity]');
            const inventoryQuantityRaw = inventoryEl
              ? parseInt(inventoryEl.getAttribute('data-inventory-quantity') || '0', 10)
              : null;
            const inventoryQuantity =
              inventoryQuantityRaw !== null
                ? window.utils?.normalizeInventoryQuantity
                  ? window.utils.normalizeInventoryQuantity(inventoryQuantityRaw)
                  : Math.max(0, inventoryQuantityRaw)
                : null;

            // Only treat as inventory issue if inventory is tracked (inventoryQuantity is not null)
            // If inventoryQuantity is null, inventory is not tracked and we shouldn't restrict quantity
            if (inventoryQuantity !== null && inventoryQuantity >= 0) {
              if (inventoryQuantity === 0) {
                outOfStockItemKeys.add(key);
                window.ckToast?.error(
                  'updateCart',
                  { message: 'This item is out of stock.' },
                  { title: 'Out of stock' }
                );
              } else if (quantity > inventoryQuantity) {
                outOfStockItemKeys.add(key);
                window.ckToast?.error(
                  'updateCart',
                  {
                    message: `Only ${inventoryQuantity} ${
                      inventoryQuantity === 1 ? 'item is' : 'items are'
                    } available. Please reduce the quantity.`,
                  },
                  { title: 'Insufficient stock' }
                );
              } else {
                outOfStockItemKeys.add(key);
                window.ckToast?.error(
                  'updateCart',
                  { message: errorMessage },
                  { title: 'Out of stock' }
                );
              }
            } else {
              // No inventory info available (inventory not tracked) - don't treat as inventory error
              // This could be a different type of error, so show generic message without marking as out of stock
              window.ckToast?.error(
                'updateCart',
                { message: errorMessage },
                { title: 'Could not update cart' }
              );
            }
          } else {
            // Not an inventory issue, but still a 422 error
            window.ckToast?.error(
              'updateCart',
              { message: errorMessage },
              { title: 'Could not update cart' }
            );
          }
        } else {
          // Non-422 error
          window.ckToast?.error(
            'updateCart',
            { message: errorMessage },
            { title: 'Could not update cart' }
          );
        }

        // Refresh the cart so UI matches Shopify's current state.
        await refreshCart();
        return;
      }
      const cart = await response.json();

      // Remove loading state before rendering so button text can update
      if (spinner) spinner.remove();
      if (checkoutButton) checkoutButton.loading = false;

      renderCartItems(cart);
      updateCartCount(cart);
    } catch (error) {
      console.error('[checkout.js] Error in updateCartItem:', error);
      window.ckToast?.error('updateCart', error, { title: 'Could not update cart' });
      await refreshCart();
    } finally {
      // Ensure loading states are cleared even if there was an error
      if (spinner) spinner.remove();
      if (checkoutButton) checkoutButton.loading = false;
    }
  }

  /**
   * Remove cart item
   */
  async function removeCartItem(key) {
    const checkoutButton = document.getElementById('cart-drawer-checkout-button');
    if (checkoutButton) {
      checkoutButton.loading = true;
    }

    // Get product info from DOM before removing (for success toast)
    const cartItemEl = cartItemsContainer.querySelector(`[data-cart-item-key="${key}"]`);
    let productInfo = null;
    if (cartItemEl) {
      let itemTitle = cartItemEl.querySelector('h3')?.textContent?.trim() || '';
      // Remove variant info if present (format: "Product Name - Variant")
      if (itemTitle.includes(' - ')) {
        itemTitle = itemTitle.split(' - ')[0].trim();
      }
      const itemImage = cartItemEl.querySelector('img')?.src;
      // Get description (the paragraph beneath the h3 title - it's inside the same anchor as h3)
      const itemLink = cartItemEl.querySelector('a[href]');
      const itemDescriptionEl = itemLink?.querySelector('h3 + p, p.text-foreground-secondary');
      const itemDescription = itemDescriptionEl?.textContent?.trim() || '';

      if (itemTitle && itemImage) {
        productInfo = {
          name: itemTitle,
          image: itemImage,
          ...(itemDescription ? { description: itemDescription } : {}),
        };
      }
    }

    try {
      const response = await fetch(config.cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const description =
          errorData.description || errorData.message || 'Failed to remove item. Please try again.';
        window.ckToast?.error(
          'removeCart',
          { message: description },
          {
            title: 'Could not remove item',
            product: productInfo, // Include product info in error toast too
          }
        );
        await refreshCart();
        return;
      }
      const cart = await response.json();

      // Remove loading state before rendering so button text can update
      if (checkoutButton) checkoutButton.loading = false;

      // Show success toast with product info
      if (productInfo) {
        window.ckToast?.success('removeCart', {
          title: 'Removed from cart',
          product: productInfo,
        });
      } else {
        window.ckToast?.success('removeCart', {
          title: 'Removed from cart',
        });
      }

      renderCartItems(cart);
      updateCartCount(cart);
    } catch (error) {
      console.error('[checkout.js] Error in removeCartItem:', error);
      window.ckToast?.error('removeCart', error, {
        title: 'Could not remove item',
        product: productInfo, // Include product info in error toast
      });
      await refreshCart();
    } finally {
      // Ensure loading is off even if there was an error
      if (checkoutButton) checkoutButton.loading = false;
    }
  }

  /**
   * Update cart count badge
   */
  function updateCartCount(cart) {
    const count = cart.item_count || 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (count > 0) {
        el.textContent = count;
        el.classList.remove('hidden');
        el.classList.add('flex');
      } else {
        el.classList.add('hidden');
        el.classList.remove('flex');
      }
    });
  }

  /**
   * Refresh cart display
   */
  async function refreshCart() {
    try {
      const cart = await fetchCart();
      if (cart) {
        renderCartItems(cart);
        updateCartCount(cart);
        updateDrawerActionsForDeliveryVisibility();
      } else {
        console.warn('[checkout.js] fetchCart returned null or undefined');
        // Render empty cart state
        renderCartItems({ items: [] });
      }
    } catch (error) {
      console.error('[checkout.js] Error refreshing cart:', error);
    }
  }

  // Initial cart load
  refreshCart();

  // Refresh cart when drawer opens
  const drawerTrigger = cartDrawer.querySelector('ck-drawer-trigger');
  if (drawerTrigger) {
    drawerTrigger.addEventListener('click', () => {
      // Clean up any pending refresh timer
      if (refreshCartTimer) {
        clearTimeout(refreshCartTimer);
      }
      refreshCartTimer = setTimeout(() => {
        refreshCart();
        refreshCartTimer = null;
      }, 100);
    });
  }

  // Expose functions for cart-subscription.js to use
  window.refreshCartAfterSubscriptionChange = refreshCartAfterSubscriptionChange;
  window.fetchCart = fetchCart;

  // Handle external cart drawer triggers (e.g., from mobile menu)
  document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      if (drawerTrigger) {
        drawerTrigger.click();
      }
    });
  });

  // Listen for cart refresh events from other scripts
  window.addEventListener('cart:refresh', () => {
    refreshCart();
  });
})();
